import * as React from 'react'
import { ReactElement } from 'react'
import FormContainer from '../../../components/Form/FormContainer'
import { useFormikContext } from 'formik'
import { FileMutationDto } from '../../../config/fileConfig'
import { Field } from '@fluentui/react-components'
import FileModuleSwitch from '../../Switch/FileModuleSwitch'
import { useSettings } from '../../../hooks/useSettings'
import { FileRateType, ModuleType, SettingsDto } from '../../../context/GlobalContext'
import { MessageBar, MessageBarType } from '@fluentui/react'
import { PivotProps } from './FileForm'
import { useTranslation } from 'react-i18next'

export type ModuleConstant = {
    defaultValue: (file: FileMutationDto, settings: SettingsDto, setFieldValue?: (field: string, value: any) => any) => any
    onAfterChecked?: (file: FileMutationDto, settings: SettingsDto, setFieldValue?: (field: string, value: any) => any) => void
    onAfterUnchecked?: (file: FileMutationDto, settings: SettingsDto, setFieldValue?: (field: string, value: any) => any) => void
}

export const ModulesConstants = new Map<ModuleType, ModuleConstant>([
    [
        ModuleType.Attributes,
        {
            defaultValue: () => ({}),
            onAfterChecked: (file, _settings, setFieldValue) => {
                if (file.attributes === undefined) {
                    if (setFieldValue) {
                        setFieldValue('attributes', [])
                    } else {
                        file.attributes = []
                    }
                }
            },
        },
    ],
    [
        ModuleType.BudgetMonitoring,
        {
            defaultValue: () => ({
                type: ModuleType.BudgetMonitoring,
                hourThreshold: 0,
                priceThreshold: 0,
                includeHours: true,
                includeTravels: false,
                includeDisbursements: false,
                includeOfferedHours: false,
                includeBilled: false,
            }),
        },
    ],
    [
        ModuleType.Insolvency,
        {
            defaultValue: (_file, settings) => ({
                type: ModuleType.Insolvency,
                factorEstate: settings.insolvency?.factorEstate ?? '1.0',
                rateEstate: settings.insolvency?.rateEstate ?? '0.00',
            }),
            onAfterChecked: (file, _settings, setFieldValue) => {
                if (setFieldValue) {
                    setFieldValue('fileRateType', FileRateType.UserTier)
                    setFieldValue('rate', undefined)
                    setFieldValue('tiers', file.tiers || [])
                } else {
                    file.fileRateType = FileRateType.UserTier
                    file.rate = undefined
                    file.tiers = file.tiers || []
                }
            },
            onAfterUnchecked: (file, settings, setFieldValue) => {
                if (setFieldValue) {
                    setFieldValue('fileRateType', settings?.fileScaffold?.fileRate?.type || FileRateType.File)
                    setFieldValue(
                        'rate',
                        (settings?.fileScaffold?.fileRate?.type === FileRateType.File && settings?.fileScaffold?.fileRate?.hourRate) || '0.00',
                    )
                    setFieldValue('tiers', undefined)
                } else {
                    file.fileRateType = settings?.fileScaffold?.fileRate?.type || FileRateType.File
                    file.rate = (settings?.fileScaffold?.fileRate?.type === FileRateType.File && settings?.fileScaffold?.fileRate?.hourRate) || '0.00'
                    file.tiers = undefined
                }
            },
        },
    ],
    [
        ModuleType.WorkInProgress,
        {
            defaultValue: () => ({
                type: ModuleType.WorkInProgress,
                percentage: 100,
                maxPrice: '0.00',
            }),
        },
    ],
])

export default function ModulePivotItem(props: PivotProps): ReactElement {
    const { t: translate } = useTranslation()
    const { settings } = useSettings()
    const { values, setFieldValue } = useFormikContext<FileMutationDto>()

    return (
        <FormContainer>
            {settings.modules
                .sort((a, b) => a.type.localeCompare(b.type))
                ?.filter(module => module.fileModule)
                .map(module => module.type)
                .map(moduleType => (
                    <Field
                        key={moduleType}
                        label={translate(`file.module.${moduleType?.toLowerCase() ?? 'unknown'}`)}
                        orientation={props.orientation}
                    >
                        <FileModuleSwitch
                            moduleType={moduleType}
                            checked={values.modules?.some(value => value.type === moduleType)}
                            defaultValues={{
                                ...(ModulesConstants.get(moduleType)?.defaultValue(values, settings, setFieldValue) ?? {
                                    type: moduleType,
                                }),
                                ...settings[moduleType.toUpperCase()],
                            }}
                            onAfterChecked={() => ModulesConstants.get(moduleType)?.onAfterChecked?.(values, settings, setFieldValue)}
                            onAfterUnchecked={() => ModulesConstants.get(moduleType)?.onAfterUnchecked?.(values, settings, setFieldValue)}
                        />
                    </Field>
                ))
                .reduce(
                    (a, b) => (
                        <>
                            {a}
                            {b}
                        </>
                    ),
                    <></>,
                )}
            {settings.modules?.length === 0 && (
                <MessageBar messageBarType={MessageBarType.info}>Er zijn geen modules geconfigureerd om te activeren.</MessageBar>
            )}
        </FormContainer>
    )
}
